import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchJobs, toggleJob, toggleFeatured, deleteJob } from '../../redux/slices/jobsSlice';
import TableHelper from '../Shared/TableHelper';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { FaTrash } from 'react-icons/fa';
import './JobsList.css';

const JobsList = () => {
  const dispatch = useDispatch();
  const { items, error } = useSelector((state) => state.jobs);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    dispatch(fetchJobs({ q: search, filter }));
  }, [search, filter, dispatch]);

  const handleDeleteJob = (jobId, applicationsCount) => {
    Swal.fire({
      title: '¿Estás completamente seguro?',
      text: `Esta oferta tiene ${applicationsCount} candidaturas. Si la eliminas, se borrará todo el historial y no podrá recuperarse.`,
      icon: 'warning',
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      denyButtonText: 'Mejor pausarla',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc3545',
      denyButtonColor: '#ffc107',
      cancelButtonColor: '#6c757d'
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteJob({ jobId })).unwrap().then(() => {
          toast.success('Oferta y candidaturas eliminadas definitivamente');
        }).catch((err) => {
          toast.error(err || 'Error al eliminar la oferta');
        });
      } else if (result.isDenied) {
        dispatch(toggleJob({ jobId })).unwrap().then(() => {
          toast.success('La oferta ha sido pausada para proteger los datos');
        }).catch((err) => {
          toast.error(err || 'Error al pausar la oferta');
        });
      }
    });
  };

  const handleToggleJob = (jobId, isActive) => {
    Swal.fire({
      title: isActive ? '¿Pausar oferta?' : '¿Activar oferta?',
      text: isActive 
        ? 'La oferta dejará de ser visible públicamente para nuevos candidatos.' 
        : 'La oferta volverá a ser visible públicamente.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: isActive ? '#d7192a' : '#28a745',
      cancelButtonColor: '#6c757d',
      confirmButtonText: isActive ? 'Sí, pausar' : 'Sí, activar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(toggleJob({ jobId })).unwrap().then(() => {
          toast.success(isActive ? 'Oferta pausada correctamente' : 'Oferta activada correctamente');
        }).catch((err) => {
          toast.error(err || 'Error al cambiar el estado de la oferta');
        });
      }
    });
  };

  const handleToggleFeatured = (jobId, isFeatured) => {
    dispatch(toggleFeatured({ jobId })).unwrap().then(() => {
      toast.success(isFeatured ? 'Destaque removido' : 'Oferta destacada con éxito');
    }).catch((err) => {
      toast.error(err || 'Error al actualizar el destaque');
    });
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Oferta',
        accessor: 'title',
        Cell: ({ row: { original: job } }) => (
          <div className="job-info-cell">
            <strong>{job.title}</strong>
            <span className="job-subtext">{job.category} · {job.city}</span>
          </div>
        ),
      },
      {
        Header: 'Empresa',
        accessor: 'company',
        Cell: ({ row: { original: job } }) => job.employer_name || job.company,
      },
      {
        Header: 'Candidaturas',
        accessor: 'applications_count',
        Cell: ({ value }) => <strong>{value}</strong>,
      },
      {
        Header: 'Visibilidad',
        accessor: 'featured',
        Cell: ({ value }) => (
          <span className={`badge ${value ? 'featured' : ''}`}>
            {value ? 'Destacada' : 'Normal'}
          </span>
        ),
      },
      {
        Header: 'Estado',
        accessor: 'active',
        Cell: ({ value }) => (
          <span className={`badge ${value ? 'active' : 'inactive'}`}>
            {value ? 'Activa' : 'Pausada'}
          </span>
        ),
      },
      {
        Header: 'Acciones',
        accessor: 'id',
        Cell: ({ row: { original: job } }) => (
          <div className="actions">
            <button
              onClick={() => handleToggleFeatured(job.id, job.featured)}
              className="btn btn-sm btn-secondary"
            >
              {job.featured ? 'Quitar destaque' : 'Destacar'}
            </button>
            <button
              onClick={() => handleToggleJob(job.id, job.active)}
              className={`btn btn-sm ${job.active ? 'btn-danger' : 'btn-success'}`}
            >
              {job.active ? 'Pausar' : 'Activar'}
            </button>
            <button
              onClick={() => handleDeleteJob(job.id, job.applications_count || 0)}
              className="btn btn-sm"
              style={{ backgroundColor: '#dc3545', color: '#fff', display: 'flex', alignItems: 'center', gap: '6px' }}
              title="Eliminar definitivamente"
            >
              <FaTrash /> Eliminar
            </button>
          </div>
        ),
      },
    ],
    [dispatch] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div className="jobs-container">
      <div className="jobs-header">
        <h1>Gestión de ofertas</h1>
        <p>Busca, destaca, pausa y supervisa publicaciones.</p>
      </div>

      <div className="jobs-toolbar">
        <input
          type="text"
          placeholder="Filtrar por título, empresa, ciudad..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="filter-select">
          <option value="">Todos los estados</option>
          <option value="active">Activas</option>
          <option value="closed">Pausadas</option>
          <option value="featured">Destacadas</option>
        </select>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="jobs-table-container">
        <TableHelper columns={columns} data={items} exportFileName="ofertas_empleo" />
      </div>
    </div>
  );
};

export default JobsList;
